"use client";

import type { speechStatus } from "@/components/chat/chat";
import { SPEECH_SYS_PROMPT } from "@/components/chat/speech-select";
import { ls } from "@/lib/utils/ls";
import type { ExpandedModel } from "@/shared/data/models";
import { useTextModels } from "@/shared/hooks/use-models";
import { useUser } from "@/shared/providers/user-provider";
import { parseAsString, useQueryState } from "nuqs";
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

const androidRegex = /android/i;

export type SttSource = "english" | "arabic";
export type TtsSource = "mute" | "english" | "arabic";

export interface GlobalContextProps {
  // we use undefined to indicate that the value is not yet known
  // since we have to wait for /models request to resolve
  // if the request fails, currentModel will be null
  currentModel: undefined | ExpandedModel | null;
  setCurrentModel: (m: ExpandedModel | null | undefined) => void;
  selectedPrompt: string | undefined;
  setSelectedPrompt: Dispatch<SetStateAction<string | undefined>>;
  initialUserPrompt: string | undefined;
  setInitialUserPrompt: Dispatch<SetStateAction<string | undefined>>;
  anonymousPromptsUsedCount: number;
  setAnonymousPromptsUsedCount: Dispatch<SetStateAction<number>>;
  isLoginDialogOpen: boolean;
  setIsLoginDialogOpen: Dispatch<SetStateAction<boolean>>;
  showLoginCardInHome: boolean;
  setShowLoginCardInHome: Dispatch<SetStateAction<boolean>>;
  isLoginEmailSent: string;
  setIsLoginEmailSent: Dispatch<SetStateAction<string>>;
  systemPrompt: string;
  setSystemPrompt: Dispatch<SetStateAction<string>>;
  autoScrollBack: boolean;
  setAutoScrollBack: Dispatch<SetStateAction<boolean>>;
  speechStatus: speechStatus;
  isResponseExtended: boolean | null;
  setIsResponseExtended: Dispatch<SetStateAction<boolean>>;
  isIframe: boolean | null;
  isFullScreenIframe: boolean | null;
  isUserLoggedIn: boolean;
  isUserRestricted: boolean;
  isProfileLoading: boolean;
  mutateModels: () => void;
  sttSource: SttSource;
  setSttSource: Dispatch<SetStateAction<SttSource>>;
  ttsSource: TtsSource;
  setTtsSource: Dispatch<SetStateAction<TtsSource>>;
  previousModel: undefined | ExpandedModel | null;
  setPreviousModel: (m: ExpandedModel | null | undefined) => void;
  selectedEnglishTtsVoice: string;
  setSelectedEnglishTtsVoice: Dispatch<SetStateAction<string>>;
  selectedArabicTtsVoice: string;
  setSelectedArabicTtsVoice: Dispatch<SetStateAction<string>>;
  isEnglishTTSTermsAccepted: boolean | null | undefined;
  isArabicTTSTermsAccepted: boolean | null | undefined;
  modelsData: ExpandedModel[];
}

// Default system prompt
export const DEFAULT_SYS_PROMPT =
  "Please try to provide useful, helpful and actionable answers.";

// Get system prompt from localStorage or use default
const getInitialSystemPrompt = () => {
  const ttsSource = getInitialTtsSource();
  if (typeof window === "undefined") {
    return ttsSource === "mute" ? DEFAULT_SYS_PROMPT : SPEECH_SYS_PROMPT;
  }
  const storedSystemPrompt = localStorage.getItem("systemPrompt");
  return (
    storedSystemPrompt ||
    (ttsSource === "mute" ? DEFAULT_SYS_PROMPT : SPEECH_SYS_PROMPT)
  );
};

const getInitialSelectedEnglishTtsVoice = () => {
  if (typeof window === "undefined") {
    return "Basil-PlayAI";
  }
  const storedVoice = localStorage.getItem("selectedEnglishTtsVoice");
  return storedVoice || "Basil-PlayAI";
};

const getInitialSelectedArabicTtsVoice = () => {
  if (typeof window === "undefined") {
    return "Nasser-PlayAI";
  }
  const storedVoice = localStorage.getItem("selectedArabicTtsVoice");
  return storedVoice || "Nasser-PlayAI";
};

// Get autoScrollBack from localStorage or use default
const getInitialAutoScrollBack = () => {
  if (typeof window === "undefined") {
    return false;
  }
  const storedAutoScrollBack = localStorage.getItem("autoScrollBack");
  return storedAutoScrollBack === "true";
};

const getInitialSttSource = () =>
  typeof window === "undefined"
    ? "english"
    : (localStorage.getItem("sttSource") as SttSource) || "english";

const getInitialTtsSource = () =>
  typeof window === "undefined"
    ? "mute"
    : (localStorage.getItem("ttsSource") as TtsSource) || "mute";

export const GlobalContext = createContext<GlobalContextProps>(
  {} as GlobalContextProps,
);

export const useGlobal = () => {
  const context = useContext(GlobalContext);

  if (!context) {
    throw new Error("useGlobal must be used within a GlobalProvider");
  }

  return context;
};

export const GlobalContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [currentModel, baseSetCurrentModel] = useState<
    ExpandedModel | null | undefined
  >(undefined);
  const [urlModelId, setUrlModelId] = useQueryState(
    "model",
    parseAsString.withDefault("compound-beta-mini"),
  );
  const [urlUserPrompt, setUrlUserPrompt] = useQueryState(
    "prompt",
    parseAsString,
  );
  const [selectedPrompt, setSelectedPrompt] = useState<string | undefined>(
    undefined,
  );
  const [initialUserPrompt, setInitialUserPrompt] = useState<
    string | undefined
  >(undefined);
  const [anonymousPromptsUsedCount, setAnonymousPromptsUsedCount] =
    useState<number>(0);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState<boolean>(false);
  const [showLoginCardInHome, setShowLoginCardInHome] =
    useState<boolean>(false);
  const [isLoginEmailSent, setIsLoginEmailSent] = useState<string>("");
  const {
    data: modelsData,
    isValidating: isModelsValidating,
    error: modelsDataError,
    mutate: mutateModels,
  } = useTextModels();
  const [systemPrompt, setSystemPrompt] = useState<string>(
    getInitialSystemPrompt,
  );
  const [autoScrollBack, setAutoScrollBack] = useState<boolean>(
    getInitialAutoScrollBack,
  );
  const [isRecordingAudio, setIsRecordingAudio] = useState<boolean>(false);
  const [isFetchingTranscriptionResponse, setIsFetchingTranscriptionResponse] =
    useState<boolean>(false);
  const [isIframe, setIsIframe] = useState<boolean | null>(null);
  const [isFullScreenIframe, setIsFullScreenIframe] = useState<boolean | null>(
    null,
  );
  const isFirstAndroidIframeLogin = useRef<boolean>(
    ls.get("repeatedAndroidIframeLogin") !== "true",
  );
  const {
    profile,
    isStytchInitialized,
    stytchUser,
    isReady,
    currentOrg,
    isProfileLoading,
  } = useUser();
  const [isResponseExtended, setIsResponseExtended] = useState<boolean>(false);
  const [sttSource, setSttSource] = useState<SttSource>(getInitialSttSource);
  const [ttsSource, setTtsSource] = useState<TtsSource>("mute");
  const [previousModel, setPreviousModel] = useState<
    ExpandedModel | null | undefined
  >(currentModel);
  const [selectedEnglishTtsVoice, setSelectedEnglishTtsVoice] =
    useState<string>(getInitialSelectedEnglishTtsVoice());
  const [selectedArabicTtsVoice, setSelectedArabicTtsVoice] = useState<string>(
    getInitialSelectedArabicTtsVoice(),
  );
  const isEnglishTTSTermsAccepted = useMemo(() => {
    const model = modelsData?.data?.find((m) => m.id === "playai-tts");
    return model ? model.is_terms_accepted : true;
  }, [modelsData?.data]);

  const isArabicTTSTermsAccepted = useMemo(() => {
    const model = modelsData?.data?.find((m) => m.id === "playai-tts-arabic");
    return model ? model.is_terms_accepted : true;
  }, [modelsData?.data]);

  const isUserLoggedIn = useMemo(() => {
    return isStytchInitialized && !!stytchUser && isReady;
  }, [isStytchInitialized, stytchUser, isReady]);
  const isUserRestricted = useMemo(
    () =>
      isUserLoggedIn &&
      profile?.user?.orgs?.data.find((org) => org.id === currentOrg)
        ?.verification_status === "RESTRICTED",
    [isUserLoggedIn, profile, currentOrg],
  );

  //handle permalinks and unset prompt in query param
  useEffect(() => {
    if (urlUserPrompt) {
      setUrlUserPrompt(null);
      setInitialUserPrompt(urlUserPrompt);
    }
  }, [urlUserPrompt]);

  // handle refresh blue.groq host site when android iframe first time login
  useEffect(() => {
    if (!isIframe) {
      return;
    }
    const userAgent = navigator.userAgent || navigator.vendor;
    const isAndroid = androidRegex.test(userAgent);
    if (!isAndroid) {
      return;
    }
    if (!isUserLoggedIn) {
      return;
    }

    if (stytchUser?.created_at) {
      if (!isFirstAndroidIframeLogin) {
        return;
      }
      const stytchUserCreatedAt = new Date(stytchUser.created_at);
      const currentDate = new Date();
      const timeFrame = new Date(
        stytchUserCreatedAt.setSeconds(stytchUserCreatedAt.getSeconds() + 5),
      );

      // refresh if the same user has already logged in
      if (currentDate < timeFrame) {
        ls.set("repeatedAndroidIframeLogin", "true");
        window.parent.postMessage("refresh-window", "*");
      }
    } else {
      ls.remove("repeatedAndroidIframeLogin");
    }
  }, [isIframe, isUserLoggedIn, stytchUser]);

  // handle full screen iframe
  useEffect(() => {
    const handleFullScreenIframe = (e: MessageEvent) => {
      if (
        e.origin !== "https://blue.groq.com" &&
        e.origin !== "https://groq.com" &&
        e.origin !== "http://localhost:5500"
      ) {
        return;
      }

      if (e.data === "toggle-full-screen-iframe") {
        setIsFullScreenIframe((pV) => !pV);
        setIsResponseExtended(false);
      }
    };
    window.addEventListener("message", handleFullScreenIframe, false);

    return () => {
      window.removeEventListener("message", handleFullScreenIframe, false);
    };
  }, []);

  useEffect(() => {
    try {
      // Check if the current window is not the top window
      setIsIframe(window.self !== window.top);
    } catch (_error) {
      // If an error occurs (likely due to cross-origin restrictions), assume it is in an iframe
      setIsIframe(true);
    }
  }, []);

  // Save system prompt to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("systemPrompt", systemPrompt);
  }, [systemPrompt]);

  // Save autoScrollBack to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("autoScrollBack", autoScrollBack.toString());
  }, [autoScrollBack]);

  // Save voice selection to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("selectedEnglishTtsVoice", selectedEnglishTtsVoice);
    localStorage.setItem("selectedArabicTtsVoice", selectedArabicTtsVoice);
  }, [selectedEnglishTtsVoice, selectedArabicTtsVoice]);

  // Handle initial model fetch
  useEffect(() => {
    if (modelsDataError) {
      toast.error("Unable to fetch models. Please try again later.");
      setCurrentModel(null);
      return;
    }

    if (currentModel || !modelsData?.data) {
      return;
    }

    const models = modelsData?.data || [];

    if (models.length === 0) {
      setCurrentModel(null);
      return;
    }

    // Preference takes into account the order of models and their number
    // Models listed at the top and with a higher number have higher preference
    const modelPreference = {
      [urlModelId || ""]: 1,
      "llama3-8b-8192": 1,
    };

    setCurrentModel(
      getInitialModel(modelPreference, models, urlModelId) || null,
    );
  }, [modelsData?.data?.length]);

  // Sync ttsSource with localStorage after component mounts
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedTtsSource = localStorage.getItem(
        "ttsSource",
      ) as TtsSource | null;
      if (storedTtsSource) {
        setTtsSource(storedTtsSource);
        if (storedTtsSource === "mute") {
          setSystemPrompt(DEFAULT_SYS_PROMPT);
        } else {
          setSystemPrompt(SPEECH_SYS_PROMPT);
        }
      }
    }
  }, [setTtsSource, setSystemPrompt]);

  const setCurrentModel = (m: ExpandedModel | null | undefined) => {
    if (m === undefined) {
      baseSetCurrentModel(undefined);
      return;
    }

    if (m == null) {
      baseSetCurrentModel(null);
      return;
    }
    if (urlModelId !== m.id) {
      setUrlModelId(m.id);
    }
    baseSetCurrentModel(m);
    if (!previousModel) {
      setPreviousModel(m);
    }
  };
  // Handle current model on terms acceptance
  useEffect(() => {
    if (!isModelsValidating && modelsData?.data && currentModel) {
      setCurrentModel(
        modelsData?.data.find((m) => m.id === currentModel?.id) || null,
      );
    }
  }, [isModelsValidating]);

  return (
    <GlobalContext.Provider
      value={{
        currentModel: currentModel,
        setCurrentModel: setCurrentModel,
        selectedPrompt: selectedPrompt,
        setSelectedPrompt: setSelectedPrompt,
        initialUserPrompt: initialUserPrompt,
        setInitialUserPrompt: setInitialUserPrompt,
        anonymousPromptsUsedCount: anonymousPromptsUsedCount,
        setAnonymousPromptsUsedCount: setAnonymousPromptsUsedCount,
        isLoginDialogOpen: isLoginDialogOpen,
        setIsLoginDialogOpen: setIsLoginDialogOpen,
        showLoginCardInHome: showLoginCardInHome,
        setShowLoginCardInHome: setShowLoginCardInHome,
        isLoginEmailSent: isLoginEmailSent,
        setIsLoginEmailSent: setIsLoginEmailSent,
        systemPrompt: systemPrompt,
        setSystemPrompt: setSystemPrompt,
        autoScrollBack: autoScrollBack,
        setAutoScrollBack: setAutoScrollBack,
        speechStatus: {
          isFetching: isFetchingTranscriptionResponse,
          setIsFetching: setIsFetchingTranscriptionResponse,
          isRecordingAudio: isRecordingAudio,
          setIsRecordingAudio: setIsRecordingAudio,
        },
        isResponseExtended: isResponseExtended,
        setIsResponseExtended: setIsResponseExtended,
        isIframe,
        isFullScreenIframe,
        isUserLoggedIn,
        isProfileLoading,
        isUserRestricted,
        mutateModels,
        sttSource,
        setSttSource,
        ttsSource,
        setTtsSource,
        previousModel,
        setPreviousModel,
        selectedEnglishTtsVoice,
        setSelectedEnglishTtsVoice,
        selectedArabicTtsVoice,
        setSelectedArabicTtsVoice,
        isEnglishTTSTermsAccepted,
        isArabicTTSTermsAccepted,
        modelsData: modelsData?.data || [],
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

// utils
const getInitialModel = (
  modelPreference: Record<string, number>,
  models: ExpandedModel[],
  modelSearchParam: string | null,
) => {
  if (!modelSearchParam) {
    const isSstSourceArabic = localStorage.getItem("sttSource") === "arabic";
    if (isSstSourceArabic) {
      const allamModel = models.find((m) => m.id === "allam-2-7b");
      if (allamModel) {
        return allamModel;
      }
    }
  }

  const randomNumber = Math.random();
  const initialModelId = Object.entries(modelPreference).reduceRight(
    (pV, [modelId, preference]) => {
      if (
        randomNumber < preference &&
        models.some((m) => m.id === modelId && m.active)
      ) {
        return modelId;
      }
      return pV;
    },
    "",
  );
  const initialModel = models.find((m) => m.id === initialModelId);
  if (!initialModel) {
    return models.find((m) => m.active) || null;
  }
  return initialModel;
};
